import { supabase } from '../lib/supabase';

// ── Portfolio Products ──────────────────────────────────────

export const portfolioProductsService = {
  async getAll(partnerId = null) {
    let query = supabase?.from('portfolio_products')?.select('*')?.order('created_at', { ascending: false });

    if (partnerId) {
      query = query?.eq('partner_id', partnerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(dbToProduct);
  },

  async bulkCreate(productsArray, partnerId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const rows = productsArray?.map(productData => ({
      product_name: productData?.productName,
      category: productData?.category,
      oem_brand: productData?.oemBrand,
      model_sku: productData?.modelSku,
      licensing_model: productData?.licensingModel,
      target_industry: productData?.targetIndustry,
      supported_use_cases: productData?.supportedUseCases,
      warranty_support: productData?.warrantySupport,
      price_range: productData?.priceRange,
      margin_category: productData?.marginCategory || 'medium',
      strategic_marking: productData?.strategicMarking || 'standard',
      ai_tags: ['Bulk Import', 'Pending Review'],
      partner_id: partnerId || null,
      catalog_product_id: productData?.id || null,
      created_by: user?.id || null,
    }));
    const { data, error } = await supabase?.from('portfolio_products')?.insert(rows)?.select();
    if (error) throw error;
    return (data || [])?.map(dbToProduct);
  },

  async create(productData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('portfolio_products')?.insert({
        product_name: productData?.productName,
        category: productData?.category,
        oem_brand: productData?.oemBrand,
        model_sku: productData?.modelSku,
        licensing_model: productData?.licensingModel,
        target_industry: productData?.targetIndustry,
        supported_use_cases: productData?.supportedUseCases,
        warranty_support: productData?.warrantySupport,
        price_range: productData?.priceRange,
        margin_category: productData?.marginCategory,
        strategic_marking: productData?.strategicMarking || 'standard',
        ai_tags: productData?.aiTags || [],
        partner_id: productData?.partnerId || null,
        created_by: user?.id || null,
      })?.select()?.single();
    if (error) throw error;
    return dbToProduct(data);
  },

  async update(id, productData) {
    const { data, error } = await supabase?.from('portfolio_products')?.update({
        product_name: productData?.productName,
        category: productData?.category,
        oem_brand: productData?.oemBrand,
        model_sku: productData?.modelSku,
        licensing_model: productData?.licensingModel,
        target_industry: productData?.targetIndustry,
        supported_use_cases: productData?.supportedUseCases,
        warranty_support: productData?.warrantySupport,
        price_range: productData?.priceRange,
        margin_category: productData?.marginCategory,
        strategic_marking: productData?.strategicMarking,
        ai_tags: productData?.aiTags || [],
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToProduct(data);
  },

  async updateStrategicMarking(id, strategicMarking) {
    const { data, error } = await supabase?.from('portfolio_products')?.update({ strategic_marking: strategicMarking })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToProduct(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('portfolio_products')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Portfolio Services ──────────────────────────────────────

export const portfolioServicesService = {
  async getAll(partnerId = null) {
    let query = supabase?.from('portfolio_services')?.select('*')?.order('created_at', { ascending: false });

    if (partnerId) {
      query = query?.eq('partner_id', partnerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(serviceData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('portfolio_services')?.insert({
        service_name: serviceData?.serviceName,
        service_category: serviceData?.serviceCategory,
        coverage_area: serviceData?.coverageArea,
        sla_levels: serviceData?.slaLevels,
        delivery_model: serviceData?.deliveryModel,
        certifications_required: serviceData?.certificationsRequired,
        industry_alignment: serviceData?.industryAlignment,
        ai_risk_rating: serviceData?.aiRiskRating || 'medium',
        strategic_marking: serviceData?.strategicMarking || 'standard',
        partner_id: serviceData?.partnerId || null,
        created_by: user?.id || null,
      })?.select()?.single();
    if (error) throw error;
    return dbToService(data);
  },

  async update(id, serviceData) {
    const { data, error } = await supabase?.from('portfolio_services')?.update({
        service_name: serviceData?.serviceName,
        service_category: serviceData?.serviceCategory,
        coverage_area: serviceData?.coverageArea,
        sla_levels: serviceData?.slaLevels,
        delivery_model: serviceData?.deliveryModel,
        certifications_required: serviceData?.certificationsRequired,
        industry_alignment: serviceData?.industryAlignment,
        ai_risk_rating: serviceData?.aiRiskRating,
        strategic_marking: serviceData?.strategicMarking,
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToService(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('portfolio_services')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Solution Bundles ────────────────────────────────────────

export const solutionBundlesService = {
  async getAll(partnerId = null) {
    let query = supabase?.from('solution_bundles')?.select('*')?.order('created_at', { ascending: false });

    if (partnerId) {
      query = query?.eq('partner_id', partnerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(bundleData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('solution_bundles')?.insert({
        bundle_name: bundleData?.bundleName,
        description: bundleData?.description,
        product_ids: bundleData?.selectedProducts || [],
        service_ids: bundleData?.selectedServices || [],
        reference_projects: bundleData?.referenceProjects,
        target_market: bundleData?.targetMarket,
        estimated_value: bundleData?.estimatedValue,
        partner_id: bundleData?.partnerId || null,
        created_by: user?.id || null,
      })?.select()?.single();
    if (error) throw error;
    return dbToBundle(data);
  },

  async update(id, bundleData) {
    const { data, error } = await supabase?.from('solution_bundles')?.update({
        bundle_name: bundleData?.bundleName,
        description: bundleData?.description,
        product_ids: bundleData?.selectedProducts || [],
        service_ids: bundleData?.selectedServices || [],
        reference_projects: bundleData?.referenceProjects,
        target_market: bundleData?.targetMarket,
        estimated_value: bundleData?.estimatedValue,
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return dbToBundle(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('solution_bundles')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Converters (snake_case DB → camelCase React) ────────────

function dbToProduct(row) {
  if (!row) return null;
  return {
    id: row?.id,
    productName: row?.product_name,
    category: row?.category,
    oemBrand: row?.oem_brand,
    modelSku: row?.model_sku,
    licensingModel: row?.licensing_model,
    targetIndustry: row?.target_industry,
    supportedUseCases: row?.supported_use_cases,
    warrantySupport: row?.warranty_support,
    priceRange: row?.price_range,
    marginCategory: row?.margin_category,
    strategicMarking: row?.strategic_marking,
    aiTags: row?.ai_tags || [],
    partnerId: row?.partner_id,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
  };
}

function dbToService(row) {
  if (!row) return null;
  return {
    id: row?.id,
    serviceName: row?.service_name,
    serviceCategory: row?.service_category,
    coverageArea: row?.coverage_area,
    slaLevels: row?.sla_levels,
    deliveryModel: row?.delivery_model,
    certificationsRequired: row?.certifications_required,
    industryAlignment: row?.industry_alignment,
    aiRiskRating: row?.ai_risk_rating,
    strategicMarking: row?.strategic_marking,
    partnerId: row?.partner_id,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
  };
}

function dbToBundle(row) {
  if (!row) return null;
  return {
    id: row?.id,
    bundleName: row?.bundle_name,
    description: row?.description,
    selectedProducts: row?.product_ids || [],
    selectedServices: row?.service_ids || [],
    referenceProjects: row?.reference_projects,
    targetMarket: row?.target_market,
    estimatedValue: row?.estimated_value,
    partnerId: row?.partner_id,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
  };
}
